import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import emailjs from '@emailjs/browser';

const ContactPage = () => {
  useScrollAnimation();
  const formRef = useRef(null);

  const EMAILJS_CONFIG = {
    SERVICE_ID: "service_gbvzeme",
    TEMPLATE_ID: "template_xc53xlq",
    PUBLIC_KEY: "ooCABeQDAfIhtu9vB",
  };

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState({ submitting: false, submitted: false, error: null });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    else if (formData.message.trim().length < 10) newErrors.message = "Message must be at least 10 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormStatus({ submitting: true, submitted: false, error: null });

    try {
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: "Not provided",
          company: "Not provided",
          subject: "Riverton Dairy Contact Form",
          message: formData.message,
          to_email: "info@rivertonmw.com",
          timestamp: new Date().toLocaleString(),
          site_source: "Riverton Dairy Website",
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      if (result.status === 200) {
        setFormStatus({ submitting: false, submitted: true, error: null });
        setFormData({ name: "", email: "", message: "" });
        setErrors({});
        setTimeout(() => setFormStatus({ submitting: false, submitted: false, error: null }), 5000);
      } else throw new Error("Failed to send email");

    } catch (error) {
      setFormStatus({ submitting: false, submitted: false, error: "Failed to send message. Please try again." });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: null });
  };

  return (
    <>
      {/* SEO Meta */}
      <Helmet>
        <title>Contact Riverton Dairy - Malawian Yogurt</title>
        <meta name="description" content="Get in touch with Riverton Dairy in Lilongwe. Send us a message or find our contact details including email, phone, and location." />
        <meta name="keywords" content="Riverton Dairy, Contact Malawi, Yogurt Contact, Malawian Yogurt, Customer Support" />
        <meta name="author" content="Riverton Dairy" />

        {/* Open Graph */}
        <meta property="og:title" content="Contact Riverton Dairy - Malawian Yogurt" />
        <meta property="og:description" content="Reach out to Riverton Dairy for inquiries, support, or feedback. Proudly Malawian yogurt made with real fruit and full-cream milk." />
        <meta property="og:image" content="/images/contact/contact-hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      {/* HERO */}
      <section className="contact-hero scroll-animate">
        <div className="container">
          <h2>Contact Us</h2>
          <p>Get in touch with Riverton Dairy</p>
        </div>
      </section>

      {/* CONTACT CONTENT */}
      <section className="contact-content scroll-animate">
        <div className="container">
          <div className="contact-container">
            <div className="contact-info-sidebar">
              <h3>Get In Touch</h3>
              <div className="contact-details">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <h4>Our Location</h4>
                    <p>Riverton Dairy, Lilongwe</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <div>
                    <h4>Phone Numbers</h4>
                    <p>+265 111 775 433<br />+265 998 955 221</p>
                  </div>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <h4>Email Address</h4>
                    <p>info@rivertonmw.com</p>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div style={{ marginTop: "20px", borderRadius: "10px", overflow: "hidden" }}>
                <iframe
                  title="Riverton Dairy Location"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d212.3513367307932!2d33.7790411!3d-13.9580864!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1921d3001ef74a2b%3A0x9a38b1b580b412f8!2sRiverton%20Dairy!5e1!3m2!1sen!2smw!4v1700000000000"
                ></iframe>
              </div>
            </div>

            <div className="contact-form">
              <h3>Send Us a Message</h3>

              {formStatus.submitted && (
                <div className="form-success">Thank you for your message! We'll get back to you soon.</div>
              )}
              {formStatus.error && <div className="form-error">{formStatus.error}</div>}

              <form onSubmit={handleSubmit} ref={formRef}>
                <div className={`form-group ${errors.name ? 'error' : ''}`}>
                  <label>Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required disabled={formStatus.submitting} />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className={`form-group ${errors.email ? 'error' : ''}`}>
                  <label>Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={formStatus.submitting} />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className={`form-group ${errors.message ? 'error' : ''}`}>
                  <label>Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows="6" required disabled={formStatus.submitting}></textarea>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>

                <button type="submit" className="btn btn-primary" disabled={formStatus.submitting}>
                  {formStatus.submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
